import { AlertCircle, Check } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { cn } from '@reflex/ui/lib/utils';
import type { DraftAutosaveStatus } from '@/hooks/agents/useAgentDraftAutosave';

export interface DraftAutosaveIndicatorProps {
  status: DraftAutosaveStatus;
  /** When false, nothing is rendered (clean form with no draft yet). */
  visible?: boolean;
  className?: string;
}

const STATUS_COPY: Record<DraftAutosaveStatus, string> = {
  saved: 'Saved',
  unsaved: 'Unsaved changes',
  saving: 'Saving...',
  error: 'Failed to save',
};

export function DraftAutosaveIndicator({
  status,
  visible = true,
  className,
}: DraftAutosaveIndicatorProps) {
  if (!visible) return null;

  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="draft-autosave-indicator"
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1',
        status === 'error' && 'text-destructive',
        className,
      )}
    >
      {status === 'saving' ? (
        <Spinner size="xs" className="text-muted-foreground" />
      ) : status === 'error' ? (
        <AlertCircle className="icon-xs" aria-hidden />
      ) : status === 'saved' ? (
        <Check className="icon-xs text-muted-foreground" aria-hidden />
      ) : null}
      <span>{STATUS_COPY[status]}</span>
    </span>
  );
}
